<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card" style="margin-top: 50px">
        <Header title="TNA Report">
          <template #right>
            <!-- Add your buttons here -->
            <div class="flex">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-info"
                  @click="downloadTemplate"
                  :disabled="isDownloading">
                  <span v-if="isDownloading">
                    <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Downloading...
                  </span>
                  <span v-else>
                    <i class="fe fe-download me-1"></i>
                    Download Template
                  </span>
                </button>
                <button 
                  type="button"
                  class="btn btn-sm btn-primary ml-1"
                  @click="showBulkUploadModal">
                  <i class="fe fe-upload me-1"></i>
                  Upload Course Mapping
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary ml-1"
                  @click="downloadHistoryTemplate"
                  :disabled="isDownloadingHistoryTemplate">
                  <span v-if="isDownloadingHistoryTemplate">
                    <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Downloading...
                  </span>
                  <span v-else>
                    <i class="fe fe-download me-1"></i>
                    History Template
                  </span>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-success ml-1"
                  @click="showTrainingHistoryUploadModal">
                  <i class="fe fe-upload me-1"></i>
                  Upload Training History
                </button>
            </div>
          </template>
        </Header>

        <div class="card-body tna-report-body">
          <div class="tna-summary-grid mb-4">
            <div class="tna-summary-card">
              <div>
                <span class="summary-eyebrow">Karyawan</span>
                <strong>{{ totalRecords }}</strong>
                <small>Dalam scope monitoring</small>
              </div>
              <i class="fe fe-users"></i>
            </div>
            <div class="tna-summary-card primary">
              <div>
                <span class="summary-eyebrow">Total Pengajuan</span>
                <strong>{{ totalSubmissions }}</strong>
                <small>Menunggu approval role aktif</small>
              </div>
              <i class="fe fe-layers"></i>
            </div>
            <div class="tna-summary-card">
              <div>
                <span class="summary-eyebrow">Page Ini</span>
                <strong>{{ currentPageSubmissions }}</strong>
                <small>Approval pada daftar tampil</small>
              </div>
              <i class="fe fe-list"></i>
            </div>
          </div>

          <div v-if="isHROrHSERole" class="filter-panel mb-4">
            <div class="filter-panel-header">
              <div>
                <h5 class="mb-1">Employee Filter</h5>
                <small class="text-muted">Daftar menampilkan karyawan BIB sesuai scope. Pengajuan yang menunggu approval tampil paling atas.</small>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="activeFilterCount === 0"
                @click="resetUserFilters"
              >
                Clear Filter
              </button>
            </div>

            <div class="filter-grid">
              <div class="filter-control">
                <label>Company</label>
                <v-select
                  :options="displayFilterOptions.company"
                  :value="'PT. BORNEO INDOBARA'"
                  placeholder="PT. BORNEO INDOBARA"
                  :clearable="false"
                  :disabled="true"
                />
              </div>

              <div class="filter-control">
                <label>Division</label>
                <v-select
                  :options="displayFilterOptions.division"
                  v-model="filterUsers.division"
                  placeholder="Select Division"
                  :clearable="true"
                />
              </div>

              <div class="filter-control">
                <label>Department</label>
                <v-select
                  :options="displayFilterOptions.department"
                  v-model="filterUsers.department"
                  placeholder="Select Department"
                  :clearable="true"
                />
              </div>

              <div class="filter-control">
                <label>Position</label>
                <v-select
                  :options="displayFilterOptions.position"
                  v-model="filterUsers.position"
                  placeholder="Select Position"
                  :clearable="true"
                />
              </div>

              <div class="filter-control filter-control-wide">
                <label>Employee</label>
                <v-select
                  :options="displayFilterOptions.employee"
                  v-model="filterUsers.employee"
                  placeholder="Select Employee"
                  label="text"
                  :reduce="option => option.value"
                  :clearable="true"
                  :filterable="false"
                  @search="onEmployeeSearch"
                >
                  <template #no-options="{ search, searching }">
                    <span v-if="!search">Ketik minimal 2 karakter untuk mencari karyawan BIB.</span>
                    <span v-else-if="searching || isSearchingEmployee">Mencari karyawan...</span>
                    <span v-else>Tidak ada karyawan BIB yang cocok.</span>
                  </template>
                </v-select>
              </div>
            </div>
          </div>

          <div class="table-toolbar mb-3">
            <div>
              <h5 class="mb-1">Employee Submission List</h5>
              <small class="text-muted">
                Semua karyawan dalam scope ditampilkan, diurutkan dari pengajuan menunggu approval terbanyak.
              </small>
            </div>
            <div class="table-search">
              <InputSearch
                v-model="searchQuery"
                placeholder="Search by name, NIK, position, department..."
                @enter="runSearch"
              />
            </div>
          </div>

          <vue-good-table
            :columns="columns"
            :rows="subordinatesDataIndexed"
            :isLoading="isLoading"
            :search-options="{ enabled: false }"
            :pagination-options="{ enabled: false }"
            mode="remote"
            styleClass="vgt-table striped bordered condensed"
            @on-sort-change="onSortChange"
          >
            <template #table-row="{ row, column }">
              <span v-if="column.field === 'total_learning'" class="submission-badge">
                {{ row.total_learning || 0 }} pengajuan
              </span>
              <span v-else-if="column.field === 'department'">
                {{ row.department || '-' }}
              </span>
              <span v-else-if="column.field === 'action'">
                <button
                  class="btn btn-sm btn-outline-primary detail-btn"
                  :disabled="!row.nik"
                  @click="row.nik && $router.push(`/tna/tna-report/${row.nik}`)"
                >
                  <i class="fe fe-eye mr-1"></i>
                  Detail
                </button>
              </span>
              <span v-else>{{ row[column.field] }}</span>
            </template>
          </vue-good-table>

          <!-- Pagination -->
          <div class="mt-4 pt-3 border-top">
            <div class="row align-items-center">
              <div class="col-md-4">
                <div class="d-flex align-items-center">
                  <span class="text-muted small mr-2">Show</span>
                  <select
                    v-model="serverParams.pageSize"
                    class="form-control form-control-sm"
                    style="width: 70px;"
                    @change="changePerPage"
                  >
                    <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                  </select>
                  <span class="text-muted small ml-2">entries</span>
                </div>
              </div>
              <div class="col-md-4 text-center">
                <small class="text-muted">
                  Showing {{ (serverParams.page - 1) * serverParams.pageSize + 1 }}
                  to {{ Math.min(serverParams.page * serverParams.pageSize, totalRecords) }}
                  of {{ totalRecords }} entries
                </small>
              </div>
              <div class="col-md-4">
                <nav>
                  <ul class="pagination pagination-sm justify-content-end mb-0">
                    <li class="page-item" :class="{ disabled: serverParams.page === 1 }">
                      <button class="page-link" @click="changePage(serverParams.page - 1)" :disabled="serverParams.page === 1">
                        <i class="fe fe-chevron-left"></i>
                      </button>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: serverParams.page === page, disabled: page === '...' }"
                    >
                      <button v-if="page === '...'" class="page-link" disabled>...</button>
                      <button v-else class="page-link" @click="changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: serverParams.page === totalPages }">
                      <button class="page-link" @click="changePage(serverParams.page + 1)" :disabled="serverParams.page === totalPages">
                        <i class="fe fe-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload Modal -->
      <b-modal 
        id="modal-bulk-upload-course-mapping" 
        centered 
        hide-footer 
        title="Upload Course Mapping File"
        size="lg">
        <form @submit.prevent="uploadFile()">
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label>Select File <span class="text-danger">*</span></label>
                <input
                  ref="fileInput"
                  type="file"
                  class="form-control"
                  accept=".xlsx"
                  @change="onFileChange"
                  required />
                <small class="text-muted">Accepted format: .xlsx (max 5MB)</small>
              </div>
            </div>
            <div class="col-12" v-if="selectedFile">
              <div class="form-group">
                <label>File Preview:</label>
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <i class="fe fe-file-text text-primary me-2" style="font-size: 2rem;"></i>
                      <div>
                        <p class="mb-0 font-weight-bold">{{ selectedFile.name }}</p>
                        <small class="text-muted">{{ formatFileSize(selectedFile.size) }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-bulk-upload-course-mapping')">
              Cancel
            </button>
            <button
              class="btn btn-primary"
              type="submit"
              :disabled="!selectedFile || isUploading">
              <span
                v-if="isUploading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              {{ isUploading ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </form>
      </b-modal>

      <b-modal
        id="modal-bulk-upload-training-history"
        centered
        hide-footer
        title="Upload Training History File"
        size="lg">
        <form @submit.prevent="uploadHistoryFile()">
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label>Select File <span class="text-danger">*</span></label>
                <input
                  ref="historyFileInput"
                  type="file"
                  class="form-control"
                  accept=".xlsx"
                  @change="onHistoryFileChange"
                  required />
                <small class="text-muted">Accepted format: .xlsx (max 5MB)</small>
              </div>
            </div>
            <div class="col-12" v-if="selectedHistoryFile">
              <div class="form-group">
                <label>File Preview:</label>
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <i class="fe fe-file-text text-primary me-2" style="font-size: 2rem;"></i>
                      <div>
                        <p class="mb-0 font-weight-bold">{{ selectedHistoryFile.name }}</p>
                        <small class="text-muted">{{ formatFileSize(selectedHistoryFile.size) }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group text-right">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-bulk-upload-training-history')">
              Cancel
            </button>
            <button
              class="btn btn-primary"
              type="submit"
              :disabled="!selectedHistoryFile || isUploadingHistory">
              <span
                v-if="isUploadingHistory"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              {{ isUploadingHistory ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </form>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { VueGoodTable } from "vue-good-table";
import "vue-good-table/dist/vue-good-table.css";
import tnaReportLogic from "./tna-report.logic";
import Header from "../components/header.vue";
import InputSearch from "../components/input-search.vue";

export default {
  name: "TNAReport",
  components: { Header, VueGoodTable, InputSearch },
  mixins: [tnaReportLogic],
  data() {
    return {
      columns: [
        { label: "No", field: "_index", type: "number", width: "65px", tdClass: "text-center", sortable: false },
        { label: "Name", field: "name", sortable: true, filterOptions: { enabled: false } },
        { label: "NIK", field: "nik", sortable: true, filterOptions: { enabled: false } },
        { label: "Position", field: "position", sortable: true, filterOptions: { enabled: false } },
        { label: "Department", field: "department", sortable: true, filterOptions: { enabled: false } },
        { label: "Pengajuan", field: "total_learning", type: "number", sortable: true, width: "150px", tdClass: "text-center", thClass: "text-center" },
        { label: "Action", field: "action", sortable: false, width: "120px", tdClass: "text-center", thClass: "text-center" },
      ],
    };
  },
  computed: {
    subordinatesDataIndexed() {
      return (this.subordinatesLearning.data || []).map((item, i) => ({
        ...item,
        _index: (this.serverParams.page - 1) * this.serverParams.pageSize + i + 1,
      }));
    },
  },
};
</script>

<style scoped>
.tna-report-body {
  padding: 22px;
}

.tna-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.tna-summary-card {
  min-height: 92px;
  border: 1px solid #e9edf5;
  border-radius: 8px;
  background: #fff;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.04);
}

.tna-summary-card.primary {
  border-color: #f2c5c9;
  background: #fffafa;
}

.tna-summary-card strong {
  display: block;
  color: #1f2937;
  font-size: 28px;
  line-height: 1.1;
  margin: 3px 0;
}

.tna-summary-card small,
.summary-eyebrow {
  color: #8ea1bf;
  font-weight: 600;
}

.summary-eyebrow {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
}

.tna-summary-card i {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #c92f3b;
  background: #fff1f2;
  font-size: 20px;
}

.filter-panel {
  border: 1px solid #e9edf5;
  border-radius: 8px;
  background: #fbfcfe;
  padding: 16px;
}

.filter-panel-header,
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.filter-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 14px;
}

.filter-control label {
  display: block;
  color: #4b587c;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.filter-control-wide {
  grid-column: span 2;
}

.table-search {
  width: min(460px, 100%);
}

.submission-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  border-radius: 999px;
  background: #fff1f2;
  color: #b4232e;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 10px;
}

.detail-btn {
  min-width: 78px;
}

@media (max-width: 992px) {
  .tna-summary-grid,
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-control-wide {
    grid-column: auto;
  }

  .filter-panel-header,
  .table-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .table-search {
    width: 100%;
  }
}
</style>
